'use client';

// Import necessary hooks and components
import React, { useState } from 'react';

import Link from 'next/link';

import {
  Info,
  CircleDollarSign,
  Tag,
  Star,
  Plane,
  RadioTower,
  PlaneTakeoff,
  Hotel,
  Truck,
  ChevronDown,
  MapPin,
  Calendar,
  ExternalLink,
  PlusCircle,
} from 'lucide-react';

import AutoHeightImage from '@/components/cards/AutoHeightImage';
import { CardCompareButtonClient } from '@/components/cards/ClientCompareButtons';
import { Card as CardType } from '@/types/Card';
import { Feature, FeatureType } from '@/types/Feature';

interface CardProps {
  card: CardType;
  numFeature?: number;
}

const featureKeys: Record<string, keyof Feature> = {
  '回饋內容：': 'feedback',
  '最高回饋：': 'max_feedback',
  '特殊條件：': 'condition',
  '適用通路：': 'channel',
};

// Helper function to get the appropriate styles for each feature type
const getFeatureTypeStyles = (type: FeatureType): string => {
  switch (type) {
    case FeatureType.CASH_BACK:
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
    case FeatureType.DISCOUNT:
      return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
    case FeatureType.FLIGHT:
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
    case FeatureType.BENEFIT:
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
    case FeatureType.MILEAGE:
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
    case FeatureType.AIRPORT_SERVICE:
      return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  }
};

// Helper function to format dates from YYYY-MM-DD to YYYY/MM/DD display format
const formatCardDate = (dateString: string): string => {
  // If the date is already in the correct format, just return it
  if (!dateString || dateString.includes('/')) return dateString;

  // Convert from YYYY-MM-DD to YYYY/MM/DD
  const [year, month, day] = dateString.split('-');
  return `${year}/${month}/${day}`;
};

const renderDetailValue = (
  value: string | string | Date | undefined
): string | string | undefined => {
  if (value instanceof Date) {
    return value.toLocaleDateString(); // Convert Date to string
  }
  return value; // Use string as is
};

// Helper function to get feature highlight text for the tooltip
// const getFeatureHighlight = (card: CardType): string => {
//   if (!card.features || card.features.length === 0) return '';

//   // Find the best cashback feature
//   const bestFeature = card.features.find(
//     f => f.type === FeatureType.CASH_BACK && f.feedback && f.feedback.includes('%')
//   );

//   if (bestFeature) {
//     return `${bestFeature.channel}: ${bestFeature.feedback}`;
//   }

//   // Fallback to the first feature
//   return `${card.features[0].channel}: ${card.features[0].feedback}`;
// };

const CreditCardIntro: React.FC<CardProps> = ({ card, numFeature = -1 }) => {
  const imageSrc = card.picture ? `/images/credit-cards/${card.picture}` : '';
  const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});
  const [isHovered, setIsHovered] = useState(false);

  const toggleFeature = (key: string) => {
    setIsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden w-full group relative transform hover:scale-[1.01]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Info indicator */}
      <div className="absolute top-3 right-3 bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 shadow-md">
        <Info className="h-5 w-5" />
      </div>

      {/* Card Info Link - covers most of the card except the bottom buttons */}
      <div
        onClick={() => (window.location.href = `/credit-cards/card/${card.key}`)}
        className="cursor-pointer"
      >
        {/* Card Header - Horizontal Layout */}
        <div className="flex flex-row items-center border-b border-gray-100 dark:border-gray-700 p-4">
          <div className="w-24 h-24 flex-shrink-0 flex items-center justify-center bg-white dark:bg-gray-700 rounded-lg p-2 shadow-sm">
            <AutoHeightImage
              src={imageSrc}
              alt={`${card.name} logo`}
              width={90}
              className="rounded-sm object-contain max-h-20"
              priority={false}
            />
          </div>
          <div className="flex-1 ml-4">
            <h2 className="font-bold text-gray-800 dark:text-white text-xl transition-colors duration-300">
              {card.name}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 transition-colors duration-300">
              {card.summary}
            </p>
            <div className="flex items-center mt-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mr-2">
                {card.issuing_bank}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                {card.payment_network}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Card Features - Single Column */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-700 dark:text-gray-200 text-sm mb-3 transition-colors duration-300">
          主要優惠
        </h3>
        <div className="space-y-3">
          {card.features?.slice(0, numFeature).map((feature, index) => (
            <div
              key={index}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border ${isOpen[`feature-${index}`] ? 'border-blue-200 dark:border-blue-800' : 'border-gray-100 dark:border-gray-700'} overflow-hidden transition-all duration-300 hover:shadow-md`}
            >
              <div
                className={`flex items-center justify-between cursor-pointer p-3 ${isOpen[`feature-${index}`] ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                onClick={() => toggleFeature(`feature-${index}`)}
              >
                <div className="flex items-center">
                  {/* Feature type indicator */}
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3 ${getFeatureTypeStyles(feature.type)}`}
                  >
                    {feature.type === FeatureType.CASH_BACK && (
                      <CircleDollarSign className="w-5 h-5" />
                    )}
                    {feature.type === FeatureType.DISCOUNT && <Tag className="w-5 h-5" />}
                    {feature.type === FeatureType.BENEFIT && <Star className="w-5 h-5" />}
                    {feature.type === FeatureType.FLIGHT && <Plane className="w-5 h-5" />}
                    {feature.type === FeatureType.MILEAGE && <RadioTower className="w-5 h-5" />}
                    {feature.type === FeatureType.AIRPORT_SERVICE && (
                      <PlaneTakeoff className="w-5 h-5" />
                    )}
                    {feature.type === FeatureType.TRAVEL && <Truck className="w-5 h-5" />}
                    {feature.type === FeatureType.HOTEL && <Hotel className="w-5 h-5" />}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-md font-bold text-gray-800 dark:text-white transition-colors duration-300">
                      {feature.feedback.split(' ')[0]}
                      <span className="ml-1 text-sm font-medium text-gray-600 dark:text-gray-300">
                        {feature.feedback.split(' ').slice(1).join(' ')}
                      </span>
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
                      {feature.simplified_condition}
                    </span>
                  </div>
                </div>

                <div className="flex-shrink-0 ml-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen[`feature-${index}`] ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-300 ${isOpen[`feature-${index}`] ? 'transform rotate-180' : ''}`}
                    />
                  </div>
                </div>
              </div>

              {/* Expanded details */}
              <div
                className={`overflow-hidden transition-all duration-300 ${isOpen[`feature-${index}`] ? 'max-h-96' : 'max-h-0'}`}
              >
                <div className="p-4 bg-white dark:bg-gray-800">
                  <ul className="space-y-3">
                    {Object.entries(featureKeys).map(([key, featureKey]) => {
                      const detailValue = feature[featureKey];
                      if (!detailValue) return null;
                      return (
                        <li key={key} className="flex">
                          <span className="font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
                            {key}
                          </span>
                          <span className="text-gray-800 dark:text-gray-200 flex-1">
                            {renderDetailValue(detailValue as string)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Card Details */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-center transition-all duration-300 hover:shadow-md">
            <div className="flex items-center justify-center mb-2">
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CircleDollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              國內回饋
            </div>
            <div className="text-lg font-bold text-gray-800 dark:text-white">
              {card.domestic_feedback.includes('%')
                ? card.domestic_feedback.split('%')[0] + '%'
                : card.domestic_feedback}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-center transition-all duration-300 hover:shadow-md">
            <div className="flex items-center justify-center mb-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <CircleDollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              海外回饋
            </div>
            <div className="text-lg font-bold text-gray-800 dark:text-white">
              {card.international_feedback.includes('%')
                ? card.international_feedback.split('%')[0] + '%'
                : card.international_feedback}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-center transition-all duration-300 hover:shadow-md">
            <div className="flex items-center justify-center mb-2">
              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              指定通路
            </div>
            <div className="text-lg font-bold text-gray-800 dark:text-white">
              {card.specific_channel_feedback.includes('%')
                ? card.specific_channel_feedback.split('%')[0] + '%'
                : card.specific_channel_feedback}
            </div>
          </div>
        </div>
      </div>

      {/* Card Footer */}
      <div className="bg-gray-50 dark:bg-gray-800 p-5 mt-2 border-t border-gray-100 dark:border-gray-700 transition-all duration-300">
        <div className="flex items-center justify-center mb-4">
          <div className="w-5 h-5 rounded-full flex items-center justify-center mr-2">
            <Calendar />
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 font-medium transition-colors duration-300">
            {`活動日期：`}
            <span className="font-semibold text-gray-800 dark:text-white">
              {`${formatCardDate(card.start_date)} ~ ${formatCardDate(card.end_date)}`}
            </span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link
              href={card.link as string}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-white border border-gray-200 dark:border-gray-600 text-center px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              前往官網
            </Link>
            <Link
              href={card.apply_link as string}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white text-center px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              手刀申請
            </Link>
            <CardCompareButtonClient
              cardKey={card.key as string}
              size="sm"
              className="w-full h-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditCardIntro;
